import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import * as stylex from '@stylexjs/stylex';
import { Alert, AuthCard, Button, FieldError, FieldRow, Input, Label, Spinner } from '@sentinel/ui';
import {
  assertSafeSsoStartUrl,
  isSafeReturnTo,
  UnauthenticatedError,
  useSession,
} from '@sentinel/auth-core';
import {
  emailStepSchema,
  passwordStepSchema,
  type EmailStepInput,
  type PasswordStepInput,
} from '../features/login/schema';
import { useDiscover } from '../features/login/use-discover';
import { useLogin } from '../features/login/use-login';
import { useApi } from '../lib/api';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

type Phase = 'email' | 'password' | 'sso';

const styles = stylex.create({
  form: { display: 'grid', gap: '0.75rem' },
  steps: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.75rem',
    color: sentinel.textSubtle,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  stepDot: {
    width: '0.375rem',
    height: '0.375rem',
    borderRadius: '50%',
    backgroundColor: sentinel.borderEmphasis,
    transitionProperty: 'background-color, transform',
    transitionDuration: '120ms',
    transitionTimingFunction: sentinel.easeOut,
  },
  stepDotActive: {
    backgroundColor: sentinel.brandPrimary,
    transform: 'scale(1.4)',
  },
  stepLabel: { color: sentinel.textMuted },
  loading: {
    display: 'grid',
    placeItems: 'center',
    paddingTop: '2rem',
    paddingBottom: '2rem',
    minHeight: '160px',
  },
});

function StepIndicator({ phase }: { phase: Phase }) {
  const { t } = useTranslation('login');
  const idx = phase === 'email' ? 0 : 1;
  return (
    <div {...stylex.props(styles.steps)}>
      <span {...stylex.props(styles.stepDot, idx >= 0 && styles.stepDotActive)} aria-hidden="true" />
      <span {...stylex.props(styles.stepLabel)}>{t('step_email')}</span>
      <span {...stylex.props(styles.stepDot, idx >= 1 && styles.stepDotActive)} aria-hidden="true" />
      <span {...stylex.props(styles.stepLabel)}>{t('step_password')}</span>
    </div>
  );
}

export function LoginPage() {
  const { t } = useTranslation('login');
  const reduceMotion = useReducedMotion();
  const searchParams = useSearch({ from: '/login' });
  const { return_to } = searchParams;
  const target = isSafeReturnTo(return_to) ? return_to : '/launch';
  const discover = useDiscover();
  const submit = useLogin();
  const api = useApi();
  const { user, loading: sessionLoading } = useSession();
  const [phase, setPhase] = useState<Phase>('email');
  const [topError, setTopError] = useState<string | null>(null);
  // Set when the MFA bridge could not complete (cookie missing/expired). Lets an
  // already-authenticated visitor fall through to the normal redirect below
  // instead of being stranded on a blank /login (see the bridge effect).
  const [bridgeFailed, setBridgeFailed] = useState(false);

  // Latch the MFA bridge flag at mount time. The bridge effect below calls
  // location.replace('/mfa/verify') to navigate away from `?mfa=required`. If
  // a session already exists at that point, the already-authenticated redirect
  // below must not also fire — both would call window.location.* and the
  // navigations race (ERR_ABORTED, or the authed redirect wrongly wins and
  // bypasses the pending MFA challenge). Using a ref (not state/derived) keeps
  // this latched even if a re-render observes mutated searchParams, without
  // causing its own re-render.
  const mountedWithMfaMarker =
    searchParams.mfa === 'required' || searchParams.mfa_enrollment_required === true;
  const isMfaBridgeMountRef = useRef(mountedWithMfaMarker);

  // Already authenticated → don't strand the user on the login form. This fires
  // on cold load when SessionProvider's silent refresh (userinfo → rotate
  // /auth/token → userinfo) restores a session from the refresh-token cookie:
  // without this, a valid-session visitor sees the login form indefinitely.
  // Mirrors the `/` index route's session-driven redirect. Full-document
  // navigation (not in-SPA) so the freshly rotated session cookie is applied
  // cleanly at the destination's route guard.
  //
  // Guard: skip when this mount was triggered by a magic-link MFA bridge
  // (`mfa=required` / `mfa_enrollment_required` on arrival). The bridge effect
  // below owns navigation to /mfa/verify or /mfa/setup (via location.replace),
  // so we must not race it here — UNLESS the bridge failed (cookie
  // missing/expired). A failed hand-off is moot once a session exists, so we
  // then fall through and complete the normal redirect rather than stranding
  // an authenticated visitor on a blank /login.
  useEffect(() => {
    if (sessionLoading || !user) return;
    if (isMfaBridgeMountRef.current && !bridgeFailed) return;
    window.location.assign(target);
  }, [user, sessionLoading, target, bridgeFailed]);

  // Magic-link → MFA bridge. When verify-magic-link returns MfaRequired (or
  // MfaEnrollmentRequired) on a non-JSON request, the backend sets a short-
  // lived HttpOnly cookie holding the mfa_token / setup_token and redirects
  // here. We consume the cookie via GET /auth/mfa/bridge (which also clears
  // it), stash the token in sessionStorage for the next route to read, and
  // navigate to /mfa/verify or /mfa/setup — never letting the raw token
  // appear in our SPA URL (which would leak via Referer / browser history /
  // proxy logs / OTel spans).
  useEffect(() => {
    const needsMfa = searchParams.mfa === 'required';
    const needsEnrollment = searchParams.mfa_enrollment_required === true;
    if (!needsMfa && !needsEnrollment) return;
    let cancelled = false;
    (async () => {
      try {
        // The OpenAPI schema declares no response body for /auth/mfa/bridge
        // (it's a one-shot consume; the body is opaque JSON), so we narrow
        // the response type at the call site rather than across the generated
        // schema.d.ts.
        const { data } = (await api.GET('/auth/mfa/bridge', {})) as {
          data?: { mfa_token: string | null; setup_token: string | null };
        };
        if (cancelled) return;
        const mfaToken = data?.mfa_token ?? null;
        const setupToken = data?.setup_token ?? null;
        // Hand off the token via sessionStorage (same-origin, same-tab, short-
        // lived) so /mfa/verify and /mfa/setup can read it without it riding
        // on the URL. The receiver clears the slot after a single read.
        try {
          if (mfaToken) sessionStorage.setItem('mfa_token', mfaToken);
          if (setupToken) sessionStorage.setItem('mfa_setup_token', setupToken);
          // Validate before storing so an unsafe value never reaches
          // sessionStorage (defense-in-depth; receivers also re-validate via
          // isSafeReturnTo before navigating).
          if (return_to && isSafeReturnTo(return_to)) {
            sessionStorage.setItem('mfa_return_to', return_to);
          }
        } catch {
          // sessionStorage unavailable (private mode quota / disabled) — fall
          // through to the error branch below.
        }
        // Use `location.replace` to both strip the `?mfa=required` marker from
        // history (so Back / reload can't loop) and navigate atomically — a
        // single history entry replaces the current one. Avoids a
        // `history.replaceState → location.assign` two-step that lets the
        // router's popstate listener fire between them and race the
        // already-authenticated redirect.
        if (needsMfa && mfaToken) {
          window.location.replace('/mfa/verify');
          return;
        }
        if (needsEnrollment && setupToken) {
          window.location.replace('/mfa/setup');
          return;
        }
        // Bridge cookie was missing or expired — strip the marker so a reload
        // doesn't loop. Mark the bridge failed: an unauthenticated visitor sees
        // the error below on the login form; an already-authenticated visitor
        // falls through to the normal redirect (see the guard above).
        window.history.replaceState(null, '', '/login');
        setBridgeFailed(true);
        setTopError(t('mfa_bridge_expired'));
      } catch {
        if (!cancelled) {
          setBridgeFailed(true);
          setTopError(t('generic_error'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // We only want this to run once on mount for the given marker; subsequent
    // re-renders should not re-trigger the bridge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailForm = useForm<EmailStepInput>({ resolver: zodResolver(emailStepSchema) });
  const pwForm = useForm<PasswordStepInput>({ resolver: zodResolver(passwordStepSchema) });

  async function onEmail(values: EmailStepInput) {
    setTopError(null);
    try {
      const r = await discover.mutateAsync(values.email);
      if (r.type === 'force_sso') {
        // HRD returns a same-origin path pointing at our backend's SSO
        // initiation endpoint (the cross-origin hop to the IdP is a 302
        // from there). Resolve and require same-origin + http(s) before
        // handing to `location.assign`, so a future bug that lets a
        // `javascript:` / `data:` / cross-origin value reach this code
        // can't run attacker code in our origin.
        try {
          // Funnel portal-initiated SSO through the /launch product resolver:
          // append `callbackURL=<origin>/launch` (+ a safe `return_to`) to the
          // HRD start URL so the OIDC callback lands on /launch and the same
          // post-login product-routing decision runs for SSO as for password
          // logins. `callbackURL` is an optional param the backend validates
          // against its allowed callback origins. We are served from the
          // gateway's own origin, which the backend accepts implicitly — that
          // origin is deliberately absent from cors.allowed_origins, because a
          // same-origin request needs no CORS entry.
          let dest = `${window.location.origin}/launch`;
          if (isSafeReturnTo(return_to)) {
            dest += `?return_to=${encodeURIComponent(return_to)}`;
          }
          // The OIDC `login_url` carries a `?login_hint=` query string (→ `&`),
          // but the SAML `login_url` has NO query string (→ `?`), so compute the
          // separator defensively rather than hardcoding `&`.
          const sep = r.login_url.includes('?') ? '&' : '?';
          const modified = `${r.login_url}${sep}callbackURL=${encodeURIComponent(dest)}`;
          // Re-validate the FINAL url (after appending) — the appended
          // same-origin absolute callbackURL must not have made the start url
          // cross-origin or non-http(s).
          assertSafeSsoStartUrl(modified, window.location.origin);
          setPhase('sso');
          window.location.assign(modified);
        } catch {
          setTopError(t('sso_unsafe_url'));
        }
        return;
      }
      pwForm.setValue('email', values.email);
      setPhase('password');
    } catch {
      setTopError(t('generic_error'));
    }
  }

  async function onPassword(values: PasswordStepInput) {
    setTopError(null);
    try {
      const result = await submit.mutateAsync(values);
      switch (result.kind) {
        case 'mfa_required': {
          const params = new URLSearchParams({ token: result.mfa_token });
          if (return_to) params.set('return_to', return_to);
          window.location.assign(`/mfa/verify?${params.toString()}`);
          return;
        }
        case 'mfa_enrollment_required': {
          const params = new URLSearchParams({ setup_token: result.setup_token });
          if (return_to) params.set('return_to', return_to);
          window.location.assign(`/mfa/setup?${params.toString()}`);
          return;
        }
        case 'tokens':
          window.location.assign(target);
          return;
      }
    } catch (e) {
      if (e instanceof UnauthenticatedError) {
        setTopError(t('invalid_credentials'));
        return;
      }
      const status = (e as { status?: number } | null)?.status;
      if (status === 401) {
        setTopError(t('invalid_credentials'));
        return;
      }
      setTopError(t('generic_error'));
    }
  }

  const subtitle = phase === 'email' ? t('subtitle_email') : undefined;

  // Authenticated visitor: the effect above is navigating away. Render nothing
  // rather than flash the login form during the redirect.
  if (user) return null;

  // Session bootstrap still in flight: SessionProvider is probing userinfo and,
  // on a cold load, silently rotating the refresh-token cookie via /auth/token.
  // Show a loading card until that resolves so we only reveal the login form
  // once we've confirmed no existing session can be restored — otherwise the
  // form flashes for a few seconds before a valid session redirects away.
  if (sessionLoading) {
    return (
      <AuthCard testId="login-loading">
        <div {...stylex.props(styles.loading)}>
          <Spinner size="lg" label={t('checking_session')} />
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('title')} subtitle={subtitle}>
      <StepIndicator phase={phase} />

      {topError ? (
        <Alert type="error" role="alert">
          {topError}
        </Alert>
      ) : null}

      <AnimatePresence mode="wait" initial={false}>
        {phase === 'email' && (
          <motion.form
            key="email-step"
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
            exit={reduceMotion ? {} : { opacity: 0, y: -8 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={emailForm.handleSubmit(onEmail)}
            {...stylex.props(styles.form)}
          >
            <FieldRow>
              <Label htmlFor="email">{t('email_label')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                aria-invalid={emailForm.formState.errors.email ? 'true' : undefined}
                {...emailForm.register('email')}
              />
              <FieldError message={emailForm.formState.errors.email?.message} />
            </FieldRow>
            <Button type="submit" disabled={discover.isPending}>
              {t('continue')}
            </Button>
          </motion.form>
        )}

        {phase === 'password' && (
          <motion.form
            key="password-step"
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
            exit={reduceMotion ? {} : { opacity: 0, y: -8 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={pwForm.handleSubmit(onPassword)}
            {...stylex.props(styles.form)}
          >
            <FieldRow>
              <Label htmlFor="password">{t('password_label')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                autoFocus
                aria-invalid={pwForm.formState.errors.password ? 'true' : undefined}
                {...pwForm.register('password')}
              />
              <FieldError message={pwForm.formState.errors.password?.message} />
            </FieldRow>
            <Button type="submit" disabled={submit.isPending}>
              {t('sign_in')}
            </Button>
          </motion.form>
        )}

        {phase === 'sso' && (
          <motion.p
            key="sso-step"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={reduceMotion ? {} : { opacity: 1 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.18 }}
          >
            {t('redirecting')}
          </motion.p>
        )}
      </AnimatePresence>
    </AuthCard>
  );
}

export const Route = createFileRoute('/login/')({
  component: LoginPage,
});
